<template>
  <div class="member-tag-search-component">
    <TagSelectorMultiple
      v-model="selectedTagIds"
      hint="選擇一個或多個標籤，系統會找出擁有這些標籤的所有會員"
      :show-selected="true"
    />

    <div v-if="selectedTagIds.length > 0" class="form-group" style="margin-top: 12px;">
      <TagMatchTypeToggle
        v-model="tagMatchType"
        label="標籤符合方式"
        :show-not-options="true"
      />
    </div>

    <!-- 排除標籤 -->
    <div v-if="showExcludeOption && selectedTagIds.length > 0" class="form-group exclude-tags-section" style="margin-top: 12px;">
      <button
        type="button"
        class="exclude-toggle-btn"
        :class="{ active: showExcludeTags }"
        @click="toggleExcludeTags"
      >
        <i :class="showExcludeTags ? 'ri-indeterminate-circle-line' : 'ri-add-circle-line'"></i>
        <span>指定排除標籤</span>
        <span v-if="excludeTagIds.length > 0" class="exclude-count-badge">{{ excludeTagIds.length }}</span>
      </button>

      <div v-if="showExcludeTags" class="exclude-tags-panel">
        <TagSelectorMultiple
          v-model="excludeTagIds"
          hint="擁有以下任一標籤的對象將被排除在搜尋結果之外"
          :show-selected="true"
        />
      </div>
    </div>

    <div v-if="showMemberFilter && selectedTagIds.length > 0" class="form-group member-filter-group" style="margin-top: 12px;">
      <label class="filter-label">篩選目標</label>
      <div class="member-filter-options">
        <label class="filter-option" :class="{ active: memberFilter === 'all' }">
          <input type="radio" v-model="memberFilter" value="all" />
          <span class="filter-icon"><i class="ri-team-line"></i></span>
          <span class="filter-text">
            <span class="filter-title">全部好友</span>
            <span class="filter-desc">包含已註冊與未註冊的 LINE 好友</span>
          </span>
        </label>
        <label class="filter-option" :class="{ active: memberFilter === 'registered' }">
          <input type="radio" v-model="memberFilter" value="registered" />
          <span class="filter-icon"><i class="ri-user-check-line"></i></span>
          <span class="filter-text">
            <span class="filter-title">已註冊會員的好友</span>
            <span class="filter-desc">LINE 好友中已建立會員資料的對象</span>
          </span>
        </label>
        <label class="filter-option" :class="{ active: memberFilter === 'unregistered' }">
          <input type="radio" v-model="memberFilter" value="unregistered" />
          <span class="filter-icon"><i class="ri-user-add-line"></i></span>
          <span class="filter-text">
            <span class="filter-title">未註冊會員的好友</span>
            <span class="filter-desc">LINE 好友中尚未建立會員資料的對象</span>
          </span>
        </label>
      </div>
    </div>

    <div v-if="selectedTagIds.length > 0" class="form-group" style="margin-top: 16px;">
      <button
        type="button"
        class="btn btn-primary"
        @click="search"
        :disabled="searching || disabled"
      >
        <span v-if="searching">
          <i class="ri-loader-4-line spin"></i>
          搜尋中...
        </span>
        <span v-else>
          <i class="ri-search-line"></i>
          <template v-if="memberFilter === 'all'">搜尋 LINE 好友</template>
          <template v-else-if="memberFilter === 'unregistered'">搜尋未註冊好友</template>
          <template v-else>搜尋會員</template>
        </span>
      </button>
    </div>

    <div v-if="searched && !searching && results.length === 0" class="no-results">
      <i class="ri-user-search-line"></i>
      <p>找不到符合標籤條件的會員</p>
    </div>

    <div v-if="results.length > 0" class="member-results">
      <div class="results-header">
        <span>找到 {{ results.length }} 位對象</span>
        <button
          type="button"
          class="btn btn-sm btn-link"
          @click="addAll"
          :disabled="disabled"
        >
          <i class="ri-add-line"></i>
          全部加入
        </button>
      </div>
      <div class="member-list">
        <div
          v-for="member in displayResults"
          :key="member.id"
          class="member-item"
          :class="{ selected: isSelected(member.id) }"
          @click="toggle(member)"
        >
          <div class="member-info">
            <div class="member-name">
              {{ member.name || '未知名稱' }}
              <span v-if="member.isMember === false" class="badge-unregistered">LINE 好友</span>
            </div>
            <div class="member-details">
              <span v-if="member.phone">{{ member.phone }}</span>
              <span v-if="member.email">{{ member.email }}</span>
              <span v-if="!member.phone && !member.email && member.lineUserId" class="line-user-id">{{ member.lineUserId }}</span>
            </div>
          </div>
          <div v-if="isSelected(member.id)" class="check-icon">
            <i class="ri-check-line"></i>
          </div>
        </div>
        <div v-if="results.length > DISPLAY_LIMIT" class="list-truncated-hint">
          <i class="ri-information-line"></i>
          僅顯示前 {{ DISPLAY_LIMIT }} 筆，共 {{ results.length }} 位。點「全部加入」可加入所有結果。
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed } from 'vue';
import { memberService } from '@/api/modules/members.js';
import { showError } from '@/utils/swal.js';
import TagSelectorMultiple from '@/components/TagSelectorMultiple.vue';
import TagMatchTypeToggle from '@/components/TagMatchTypeToggle.vue';

export default {
  name: 'MemberTagSearch',
  components: { TagSelectorMultiple, TagMatchTypeToggle },
  props: {
    modelValue: {
      type: Array,
      default: () => []
    },
    disabled: {
      type: Boolean,
      default: false
    },
    showMemberFilter: {
      type: Boolean,
      default: true
    },
    showExcludeOption: {
      type: Boolean,
      default: true
    },
    lineConfigId: {
      type: String,
      default: null
    }
  },
  emits: ['update:modelValue'],
  setup(props, { emit, expose }) {
    const selectedTagIds = ref([]);
    const tagMatchType = ref('OR');
    const memberFilter = ref('registered');
    const excludeTagIds = ref([]);
    const showExcludeTags = ref(false);
    const results = ref([]);
    const searching = ref(false);
    const searched = ref(false);

    const toggleExcludeTags = () => {
      showExcludeTags.value = !showExcludeTags.value;
      if (!showExcludeTags.value) {
        excludeTagIds.value = [];
      }
    };

    const isSelected = (memberId) => props.modelValue.some(m => m.id === memberId);

    const toggle = (member) => {
      const current = [...props.modelValue];
      const index = current.findIndex(m => m.id === member.id);
      if (index > -1) {
        current.splice(index, 1);
      } else {
        current.push(member);
      }
      emit('update:modelValue', current);
    };

    const addAll = () => {
      const current = [...props.modelValue];
      results.value.forEach(member => {
        if (!current.some(m => m.id === member.id)) current.push(member);
      });
      emit('update:modelValue', current);
    };

    const DISPLAY_LIMIT = 100;
    const displayResults = computed(() => results.value.slice(0, DISPLAY_LIMIT));

    const search = async () => {
      if (selectedTagIds.value.length === 0) {
        showError('請至少選擇一個標籤');
        return;
      }
      searching.value = true;
      searched.value = false;
      results.value = [];
      try {
        const response = await memberService.getMembersByTags(
          selectedTagIds.value, 10000, tagMatchType.value,
          memberFilter.value,
          excludeTagIds.value.length > 0 ? excludeTagIds.value : [],
          props.lineConfigId || null
        );
        if (response?.success) {
          results.value = response.data || [];
        } else {
          showError(response?.message || '搜尋失敗');
        }
      } catch (err) {
        console.error('根據標籤搜尋會員失敗:', err);
        showError('搜尋失敗');
      } finally {
        searching.value = false;
        searched.value = true;
      }
    };

    const reset = () => {
      selectedTagIds.value = [];
      tagMatchType.value = 'OR';
      memberFilter.value = 'registered';
      excludeTagIds.value = [];
      showExcludeTags.value = false;
      results.value = [];
      searched.value = false;
    };

    expose({ reset });

    return {
      selectedTagIds,
      tagMatchType,
      memberFilter,
      excludeTagIds,
      showExcludeTags,
      toggleExcludeTags,
      results,
      displayResults,
      DISPLAY_LIMIT,
      searching,
      searched,
      isSelected,
      toggle,
      addAll,
      search
    };
  }
};
</script>

<style scoped>
.member-results {
  margin-top: 16px;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  overflow: hidden;
}

.results-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: #f8f9fa;
  font-size: 14px;
  color: #666;
  border-bottom: 1px solid #e9ecef;
}

.member-list {
  max-height: 300px;
  overflow-y: auto;
}

.member-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  cursor: pointer;
  transition: background-color 0.2s;
  border-bottom: 1px solid #f0f0f0;
}

.member-item:hover { background: #f8f9fa; }
.member-item.selected { background: #e7f3ff; border-color: #b3d9ff; }
.member-item:last-child { border-bottom: none; }

.member-info { flex: 1; }

.member-name {
  font-weight: 500;
  color: #333;
  margin-bottom: 4px;
}

.member-details {
  font-size: 13px;
  color: #666;
  display: flex;
  gap: 12px;
}

.check-icon {
  color: #28a745;
  font-size: 20px;
}

.list-truncated-hint {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 16px;
  font-size: 13px;
  color: #6c757d;
  background: #f8f9fa;
  border-top: 1px solid #e9ecef;
}

.no-results {
  text-align: center;
  padding: 40px 20px;
  color: #999;
}

.no-results i {
  font-size: 48px;
  margin-bottom: 12px;
  display: block;
}

.member-filter-group {
  width: 100%;
}

.filter-label {
  display: block;
  font-size: 14px;
  font-weight: 500;
  color: #333;
  margin-bottom: 8px;
}

.member-filter-options {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.filter-option {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 14px;
  border: 2px solid #e9ecef;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
  background: #fff;
}

.filter-option:hover {
  border-color: #adb5bd;
  background: #f8f9fa;
}

.filter-option.active {
  border-color: #0d6efd;
  background: #f0f5ff;
}

.filter-option input[type="radio"] {
  display: none;
}

.filter-icon {
  font-size: 20px;
  color: #6c757d;
  flex-shrink: 0;
  width: 24px;
  text-align: center;
}

.filter-option.active .filter-icon {
  color: #0d6efd;
}

.filter-text {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.filter-title {
  font-size: 14px;
  font-weight: 500;
  color: #333;
}

.filter-option.active .filter-title {
  color: #0d6efd;
}

.filter-desc {
  font-size: 12px;
  color: #888;
}

.badge-unregistered {
  display: inline-block;
  font-size: 11px;
  font-weight: 500;
  color: #0958d9;
  background: #e6f4ff;
  border: 1px solid #91caff;
  border-radius: 4px;
  padding: 1px 6px;
  margin-left: 6px;
  vertical-align: middle;
}

.line-user-id {
  font-size: 11px;
  color: #aaa;
  font-family: monospace;
}

.exclude-tags-section {
  width: 100%;
}

.exclude-toggle-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  font-size: 13px;
  font-weight: 500;
  color: #dc3545;
  background: #fff5f5;
  border: 1.5px dashed #f5c2c7;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
}

.exclude-toggle-btn:hover {
  background: #ffe0e3;
  border-color: #dc3545;
}

.exclude-toggle-btn.active {
  background: #fff0f1;
  border-style: solid;
  border-color: #dc3545;
}

.exclude-toggle-btn i {
  font-size: 16px;
}

.exclude-count-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  padding: 0 5px;
  font-size: 11px;
  font-weight: 700;
  color: #fff;
  background: #dc3545;
  border-radius: 10px;
}

.exclude-tags-panel {
  margin-top: 10px;
  padding: 14px;
  background: #fff8f8;
  border: 1.5px solid #f5c2c7;
  border-radius: 8px;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.spin {
  display: inline-block;
  animation: spin 1s linear infinite;
}
</style>
